import { useState } from "react";
import { Loader2, Check, Link2, Unlink, Pencil, Trash2, RotateCcw, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { ReceiptThumb, ReceiptPreviewDialog, PreviewButtons } from "./ReceiptPreview";
import { ReceiptEditDialog } from "./ReceiptEditDialog";
import { TransactionPicker, TxLine } from "./TransactionPicker";
import { api, errMsg } from "../lib/api";
import { fmtDate, fmtNum } from "../lib/format";

const CONF = { hoch: "bg-emerald-50 text-emerald-800 border-emerald-200", mittel: "bg-amber-50 text-amber-800 border-amber-200", niedrig: "bg-slate-100 text-slate-600 border-slate-300" };

export const ConfBadge = ({ c }) => <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[11px] font-semibold ${CONF[c] || CONF.niedrig}`}>{c}</span>;

export const ReceiptSummary = ({ rc }) => {
  const e = rc.extracted || {};
  if (rc.ai_status === "pending" || rc.ai_status === "running")
    return <div className="flex items-center gap-2 text-sm text-slate-600" data-testid={`receipt-reading-${rc.id}`}><Loader2 className="h-4 w-4 animate-spin" /> Beleg wird gelesen …</div>;
  if (rc.ai_status === "failed") return <div className="text-sm text-red-700">{rc.ai_error}</div>;
  return (
    <div className="min-w-0" data-testid={`receipt-summary-${rc.id}`}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="truncate font-semibold text-slate-900" data-testid={`receipt-vendor-${rc.id}`}>{e.vendor || "Unbekannter Aussteller"}</span>
        <span className="font-num font-semibold text-slate-900" data-testid={`receipt-amount-${rc.id}`}>{fmtNum(e.gross_total)} {e.currency || "EUR"}</span>
      </div>
      <div className="text-xs text-slate-500">
        {e.document_type} · {fmtDate(e.invoice_date)}{e.invoice_number ? ` · Nr. ${e.invoice_number}` : ""}
        {e.vat_amount ? ` · USt ${fmtNum(e.vat_amount)}` : ""}
      </div>
      {e.description && <div className="mt-0.5 line-clamp-2 text-sm text-slate-600">{e.description}</div>}
    </div>
  );
};

const Suggestion = ({ s, onConfirm, rid, idx }) => (
  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`receipt-suggestion-${rid}-${idx}`}>
    <ConfBadge c={s.confidence} />
    <TxLine t={s.transaction} />
    <Button size="sm" onClick={onConfirm} data-testid={`confirm-suggestion-${rid}-${idx}`} className="h-8 gap-1 bg-slate-900 hover:bg-slate-800"><Check className="h-3.5 w-3.5" />Bestätigen</Button>
    <div className="w-full text-xs text-slate-500">
      {s.amount_note && <AlertTriangle className="mr-1 inline h-3 w-3 text-amber-600" />}{s.reason}
    </div>
  </div>
);

const DeleteReceipt = ({ rc, onDone }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="ghost" size="sm" data-testid={`delete-receipt-btn-${rc.id}`} className="h-8 gap-1 px-2 text-red-700 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" />Löschen</Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Beleg löschen?</AlertDialogTitle>
        <AlertDialogDescription>Der Beleg wird endgültig entfernt und von allen Buchungen gelöst.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
        <AlertDialogAction data-testid={`confirm-delete-receipt-${rc.id}`} className="bg-red-700 hover:bg-red-800"
          onClick={async () => { try { await api.deleteReceipt(rc.id); toast.success("Beleg gelöscht"); onDone(); } catch (e) { toast.error(errMsg(e)); } }}>Löschen</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const ReceiptCard = ({ rc, onChanged, onConfirmed, compact, contextTxId }) => {
  const [preview, setPreview] = useState(false);
  const [edit, setEdit] = useState(false);
  const [pick, setPick] = useState(false);
  const run = async (fn, msg, after) => {
    try { await fn(); if (msg) toast.success(msg); onChanged(); after?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  const linked = rc.linked_transactions || [];
  const suggestions = compact ? [] : rc.suggestions || [];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 fade-up" data-testid={`receipt-card-${rc.id}`}>
      <div className="flex gap-3">
        <ReceiptThumb rc={rc} onClick={() => setPreview(true)} />
        <div className="min-w-0 flex-1">
          <ReceiptSummary rc={rc} />
          <div className="mt-1 truncate text-xs text-slate-400">{rc.file.original_filename}</div>
        </div>
      </div>
      {linked.length > 0 && (
        <div className="mt-3 grid gap-1.5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Zugeordnet zu</div>
          {linked.map((t) => (
            <div key={t.id} className="flex items-center gap-2 rounded-lg bg-emerald-50/60 px-3 py-1.5" data-testid={`receipt-linked-${rc.id}-${t.id}`}>
              <TxLine t={t} />
              <Button variant="ghost" size="sm" title="Zuordnung lösen" data-testid={`unlink-receipt-${rc.id}-${t.id}`} className="h-7 px-2 text-slate-600"
                onClick={() => run(() => api.unlinkReceipt(rc.id, t.id), "Zuordnung gelöst")}><Unlink className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      )}
      {rc.ai_status === "done" && !compact && linked.length === 0 && (
        <div className="mt-3 grid gap-1.5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{suggestions.length ? "Vorschläge" : "Keine passende Buchung gefunden – nicht zugeordnet"}</div>
          {suggestions.map((s, i) => (
            <Suggestion key={s.transaction_id} s={s} rid={rc.id} idx={i}
              onConfirm={() => run(() => api.linkReceipt(rc.id, s.transaction_id), "Beleg zugeordnet – Beleg vorhanden", () => onConfirmed?.(s.transaction_id))} />
          ))}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-slate-100 pt-2">
        <PreviewButtons rc={rc} onPreview={() => setPreview(true)} />
        <Button variant="ghost" size="sm" onClick={() => setPick(true)} data-testid={`manual-link-btn-${rc.id}`} className="h-8 gap-1 px-2 text-slate-600"><Link2 className="h-3.5 w-3.5" />{linked.length ? "Weitere Buchung" : "Manuell zuordnen"}</Button>
        <Button variant="ghost" size="sm" onClick={() => setEdit(true)} disabled={rc.ai_status !== "done" && rc.ai_status !== "failed"} data-testid={`edit-receipt-btn-${rc.id}`} className="h-8 gap-1 px-2 text-slate-600"><Pencil className="h-3.5 w-3.5" />Bearbeiten</Button>
        <Button variant="ghost" size="sm" onClick={() => run(() => api.rereadReceipt(rc.id), "Beleg wird neu gelesen")} data-testid={`reread-receipt-btn-${rc.id}`} className="h-8 gap-1 px-2 text-slate-600"><RotateCcw className="h-3.5 w-3.5" />Neu lesen</Button>
        {contextTxId && (
          <Button variant="ghost" size="sm" data-testid={`unlink-here-btn-${rc.id}`} className="h-8 gap-1 px-2 text-slate-600"
            onClick={() => run(() => api.unlinkReceipt(rc.id, contextTxId), "Zuordnung gelöst – Beleg liegt wieder im Eingang")}><Unlink className="h-3.5 w-3.5" />Lösen</Button>
        )}
        <DeleteReceipt rc={rc} onDone={onChanged} />
      </div>
      <ReceiptPreviewDialog rc={rc} open={preview} onOpenChange={setPreview} />
      <ReceiptEditDialog rc={rc} open={edit} onOpenChange={setEdit} onSave={(body) => run(() => api.patchReceipt(rc.id, body), "Belegdaten gespeichert", () => setEdit(false))} />
      <TransactionPicker open={pick} onOpenChange={setPick} initialAmount={rc.extracted?.currency === "EUR" ? rc.extracted?.gross_total : null}
        onPick={(t) => run(() => api.linkReceipt(rc.id, t.id), "Beleg zugeordnet", () => setPick(false))} />
    </div>
  );
};
